import * as csstree from 'css-tree'

const CUSTOM_CSS_LIMIT = 20000
const ALLOWED_AT_RULES = new Set(['media', 'supports', 'keyframes'])
const DISALLOWED_PROPERTIES = new Set(['behavior', '-moz-binding'])

export class ProfileCssValidationError extends Error {
  validationErrors: string[]

  constructor(errors: string[]) {
    super(errors[0] || 'Profile CSS is invalid.')
    this.name = 'ProfileCssValidationError'
    this.validationErrors = errors
  }
}

function toLowerValue(value: unknown) {
  return String(value ?? '').trim().toLowerCase()
}

function parseTimeToken(raw: string) {
  const match = raw.trim().match(/^(-?\d+(?:\.\d+)?)(ms|s)$/i)
  if (!match) return null
  const numeric = Number(match[1])
  if (!Number.isFinite(numeric)) return null
  return match[2].toLowerCase() === 's' ? numeric * 1000 : numeric
}

function validateAnimationShorthand(valueText: string, errors: string[]) {
  if (!valueText) return

  const segments = valueText.split(',')
  for (const segment of segments) {
    if (/\binfinite\b/i.test(segment)) {
      errors.push('animation cannot use infinite iteration counts.')
      continue
    }

    const numericIterations = segment.match(/(^|\s)(\d+(?:\.\d+)?)(?=\s|$)/g) || []
    for (const token of numericIterations) {
      const iterationValue = Number(token.trim())
      if (Number.isFinite(iterationValue) && iterationValue > 10) {
        errors.push('animation cannot iterate more than 10 times.')
        break
      }
    }

    const timeTokens = segment.match(/-?\d+(?:\.\d+)?(?:ms|s)\b/gi) || []
    if (timeTokens.length) {
      const durationMs = parseTimeToken(timeTokens[0] ?? '')
      if (durationMs != null && durationMs < 200) {
        errors.push('animation duration must be at least 200ms.')
      }
    }
  }
}

function validateAnimationList(valueText: string, errors: string[], kind: 'duration' | 'count') {
  if (!valueText) return

  const segments = valueText.split(',')
  for (const segment of segments) {
    const trimmed = segment.trim()
    if (!trimmed) continue

    if (kind === 'count') {
      if (/^infinite$/i.test(trimmed)) {
        errors.push('animation-iteration-count cannot be infinite.')
        continue
      }

      const iterationValue = Number(trimmed)
      if (!Number.isFinite(iterationValue) || iterationValue > 10) {
        errors.push('animation-iteration-count must be a finite number up to 10.')
      }
      continue
    }

    const durationMs = parseTimeToken(trimmed)
    if (durationMs == null || durationMs < 200) {
      errors.push('animation-duration must be at least 200ms.')
    }
  }
}

function collectValueText(valueNode: unknown) {
  try {
    return csstree.generate(valueNode as csstree.CssNode)
  } catch {
    return ''
  }
}

export function sanitizeProfileCss(input: unknown) {
  const css = typeof input === 'string' ? input.trim() : ''
  const errors: string[] = []

  if (!css) {
    return { css: '', errors }
  }

  if (css.length > CUSTOM_CSS_LIMIT) {
    return {
      css: '',
      errors: [`Custom CSS cannot exceed ${CUSTOM_CSS_LIMIT} characters.`],
    }
  }

  let ast: csstree.CssNode

  try {
    ast = csstree.parse(css, {
      positions: false,
      parseAtrulePrelude: true,
      parseValue: true,
    })
  } catch (error) {
    return {
      css: '',
      errors: [error instanceof Error ? error.message : 'Custom CSS could not be parsed.'],
    }
  }

  csstree.walk(ast, (node: csstree.CssNode) => {
    if (node.type === 'Atrule') {
      const atRuleName = toLowerValue(node.name)
      if (!ALLOWED_AT_RULES.has(atRuleName)) {
        errors.push(`@${atRuleName} is not allowed.`)
      }
      return
    }

    if (node.type !== 'Declaration') return

    const property = toLowerValue(node.property)
    const valueText = collectValueText(node.value)
    const valueTextLower = valueText.toLowerCase()

    if (DISALLOWED_PROPERTIES.has(property)) {
      errors.push(`${property} is not allowed.`)
    }

    if (property === 'background-image') {
      errors.push('background-image is not allowed in profile CSS v1.')
    }

    if (property === 'position' && /\b(?:fixed|sticky)\b/.test(valueTextLower)) {
      errors.push('position cannot use fixed or sticky.')
    }

    if (property === 'pointer-events' && /\bnone\b/.test(valueTextLower)) {
      errors.push('pointer-events: none is not allowed.')
    }

    if (property === 'z-index') {
      const numericValue = Number(valueText)
      if (!Number.isNaN(numericValue) && numericValue > 100) {
        errors.push('z-index cannot exceed 100.')
      }
    }

    if (property === 'animation') {
      validateAnimationShorthand(valueText, errors)
    } else if (property === 'animation-duration') {
      validateAnimationList(valueText, errors, 'duration')
    } else if (property === 'animation-iteration-count') {
      validateAnimationList(valueText, errors, 'count')
    }

    if (!node.value) return

    csstree.walk(node.value as csstree.CssNode, (valueNode: csstree.CssNode) => {
      if (valueNode.type === 'Url') {
        errors.push('url() is not allowed in profile CSS.')
        return
      }

      if (valueNode.type !== 'Function') return

      const functionName = toLowerValue(valueNode.name)
      if (functionName === 'url') {
        errors.push('url() is not allowed in profile CSS.')
      }
      if (functionName === 'expression') {
        errors.push('expression() is not allowed in profile CSS.')
      }
    })

    if (valueTextLower.includes('-moz-binding')) {
      errors.push('-moz-binding is not allowed.')
    }
  })

  const uniqueErrors = [...new Set(errors)]
  return {
    css: uniqueErrors.length ? '' : csstree.generate(ast),
    errors: uniqueErrors,
  }
}

export function validateProfileCss(input: unknown) {
  const result = sanitizeProfileCss(input)
  if (result.errors.length) {
    throw new ProfileCssValidationError(result.errors)
  }
  return result.css
}
